/**
 * PhoneInput.tsx
 *
 * The one phone field for every BotSpot form (Rob, 2026-10-19 and 2026-10-21).
 *
 * Rob, 2026-10-21: "I don't like how it doesn't save the number and maybe it
 * should tell them there's something wrong with the number in case they
 * actually typed it wrong, I would not want to lose their number. On the
 * other hand I also wouldn't want it to be annoying bc then we lose the whole
 * lead. Europe and other countries requiring + might be an issue too no? ...
 * We should have one phone number form on the entire website."
 *
 * WHY THIS EXISTS:
 * - One field owns the country picker, the input, the soft hint, formatting
 *   and the value the page gets. Pages hold no phone logic of their own.
 * - People type their national number for the country beside the field (it
 *   starts on the visitor's country). A + number, typed, pasted or autofilled,
 *   switches the country itself.
 * - The page gets E.164 when the number is valid for that country, otherwise
 *   exactly what was typed. The server keeps an unreadable number as typed and
 *   never texts it, so no number is ever lost.
 *
 * IMPORTANT BEHAVIORS:
 * - Never blocks, never marks the field invalid, never shows a red error.
 *   Leaving the field with a number that is not valid shows one soft hint;
 *   it clears the moment the number becomes valid.
 * - Formats on blur, not while typing (as-you-type formatting moved the caret
 *   and fought backspace). Blur only changes the display; it never calls
 *   onValueChange, because pages reset number-specific SMS consent whenever
 *   the number changes.
 * - Carries no visual style of its own for the input: the page passes its
 *   existing input class. The picker sits inside the input and takes the
 *   input's own text color, so every form keeps its look. The country list
 *   uses design-system tokens (bsui) in the light or dark theme that matches
 *   the input.
 */
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  callingCodeFor,
  countryFlag,
  countryFromInternationalInput,
  countryOfValidNumber,
  formatPhoneForDisplay,
  guessVisitorCountry,
  isPhoneLibraryLoaded,
  listPhoneCountries,
  loadPhoneLibrary,
  toValidE164,
  type PhoneCountry,
} from '../../utils/phoneNumber';
import './PhoneInput.css';

export const PHONE_DOUBLE_CHECK_HINT = 'Double-check this number so we can reach you.';
/** Matches botspot_node user.phone (varchar(64)). */
const PHONE_MAX_LENGTH = 64;
// Built from a variable on purpose: the SMS consent coverage guard finds the
// pages that collect phones by a literal tel type attribute in their source.
const TEL_INPUT_TYPE = 'tel';
const PINNED_COUNTRIES = ['US', 'CA', 'GB', 'AU'];

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange' | 'placeholder' | 'type'
> & {
  /** The page's stored value (E.164, raw text, or empty). */
  value: string | null | undefined;
  /** Receives E.164 when valid, otherwise exactly what was typed. */
  onValueChange: (value: string) => void;
  /** Starting country; defaults to the visitor's country from the browser. */
  defaultCountry?: PhoneCountry;
  /** Accepted for the SMS consent coverage guard; the field is always tel. */
  type?: 'tel';
};

type Measure = { left: number; padding: number; color: string; dark: boolean };

const parsePixels = (value: string | null | undefined, fallback: number): number => {
  const parsed = Number.parseFloat(String(value || ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const isDarkText = (color: string): boolean => {
  const match = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(color || '');
  if (!match) return false;
  const [r, g, b] = match.slice(1, 4).map(Number);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
};

const startingCountry = (value: string, fallback: PhoneCountry): PhoneCountry =>
  countryFromInternationalInput(value) || countryOfValidNumber(value, fallback) || fallback;

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  {
    value,
    onValueChange,
    defaultCountry,
    onBlur,
    onFocus,
    inputMode = 'tel',
    autoComplete = 'tel',
    className,
    style,
    disabled,
    id,
    type: _type,
    'aria-describedby': describedBy,
    ...inputProps
  },
  forwardedRef,
) {
  const external = value ?? '';
  const fallbackCountry = useMemo(
    () => (defaultCountry || guessVisitorCountry()).toUpperCase(),
    [defaultCountry],
  );
  const [libraryReady, setLibraryReady] = useState(isPhoneLibraryLoaded);
  const [country, setCountry] = useState<PhoneCountry>(() =>
    startingCountry(external, fallbackCountry),
  );
  const [text, setText] = useState(() => formatPhoneForDisplay(external, country));
  const [showHint, setShowHint] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [measure, setMeasure] = useState<Measure | null>(null);
  const [panelRect, setPanelRect] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const lastEmitted = useRef(external);
  const focused = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const baseId = useId();
  const hintId = `${id || baseId}-phone-hint`;
  const listId = `${id || baseId}-phone-countries`;

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (forwardedRef instanceof Function) forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  useEffect(() => {
    if (libraryReady) return undefined;
    let live = true;
    loadPhoneLibrary().then((library) => {
      if (live && library) setLibraryReady(true);
    });
    return () => {
      live = false;
    };
  }, [libraryReady]);

  // The page changed the value itself (prefill, saved profile, reset).
  useEffect(() => {
    if (external === lastEmitted.current) return;
    lastEmitted.current = external;
    const nextCountry = external ? startingCountry(external, country) : country;
    setCountry(nextCountry);
    setText(formatPhoneForDisplay(external, nextCountry));
    setShowHint(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external]);

  // Metadata arrived after a prefilled value rendered: place and tidy it.
  useEffect(() => {
    if (!libraryReady || focused.current) return;
    const nextCountry = text ? startingCountry(text, country) : country;
    setCountry(nextCountry);
    setText((current) => formatPhoneForDisplay(current, nextCountry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryReady]);

  // Sit the picker inside the input, in the input's own color and spacing.
  useLayoutEffect(() => {
    const input = inputRef.current;
    const button = buttonRef.current;
    if (!input || !button) return;
    const computed = window.getComputedStyle(input);
    const basePadding = measure?.padding ?? parsePixels(computed.paddingLeft, 12);
    const left = Math.max(4, basePadding - 6);
    const width = button.offsetWidth || 64;
    const next = {
      left,
      padding: basePadding,
      color: computed.color || '',
      dark: isDarkText(computed.color),
    };
    if (!measure || measure.color !== next.color || measure.left !== next.left) setMeasure(next);
    input.style.paddingLeft = `${left + width + 6}px`;
  });

  const emit = (typed: string, forCountry: PhoneCountry) => {
    const submitValue = toValidE164(typed, forCountry) ?? typed;
    lastEmitted.current = submitValue;
    onValueChange(submitValue);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;
    setText(typed);
    const switched = countryFromInternationalInput(typed);
    const nextCountry = switched || country;
    if (switched && switched !== country) setCountry(switched);
    if (showHint && (!typed.trim() || toValidE164(typed, nextCountry))) setShowHint(false);
    emit(typed, nextCountry);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    focused.current = true;
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    focused.current = false;
    const trimmed = text.trim();
    setShowHint(Boolean(trimmed) && !toValidE164(trimmed, country));
    setText((current) => formatPhoneForDisplay(current, country));
    onBlur?.(event);
  };

  const countries = useMemo(() => (libraryReady ? listPhoneCountries() : []), [libraryReady]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase().replace(/^\+/, '');
    const all = query
      ? countries.filter(
          (option) =>
            option.name.toLowerCase().includes(query) ||
            option.code.toLowerCase() === query ||
            option.callingCode.startsWith(query),
        )
      : [
          ...PINNED_COUNTRIES.map((code) =>
            countries.find((option) => option.code === code),
          ).filter((option): option is NonNullable<typeof option> => Boolean(option)),
          ...countries.filter((option) => !PINNED_COUNTRIES.includes(option.code)),
        ];
    return all;
  }, [countries, search]);

  const placePanel = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(Math.max(rect.width, 280), window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setPanelRect({ top: rect.bottom + 4, left, width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    placePanel();
    const close = (event: MouseEvent | TouchEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (!target || buttonRef.current?.contains(target)) return;
      if (document.getElementById(listId)?.parentElement?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    searchRef.current?.focus();
    return () => {
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open, placePanel, listId]);

  const chooseCountry = (code: PhoneCountry) => {
    setOpen(false);
    setSearch('');
    if (code !== country) {
      setCountry(code);
      // The same digits mean a different number in another country.
      if (text.trim() && !text.trim().startsWith('+')) {
        emit(text, code);
        if (showHint) setShowHint(!toValidE164(text, code));
      }
    }
    inputRef.current?.focus();
  };

  const handleListKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setSearch('');
      buttonRef.current?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) chooseCountry(option.code);
    }
  };

  const code = callingCodeFor(country);
  const countryName = countries.find((option) => option.code === country)?.name || country;
  const describedByIds =
    [describedBy, showHint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <span className="bs-phone-field">
      <span className="bs-phone-field__control" ref={wrapRef}>
        <input
          ref={setInputRef}
          {...inputProps}
          id={id}
          type={TEL_INPUT_TYPE}
          className={className}
          style={style}
          disabled={disabled}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={PHONE_MAX_LENGTH}
          placeholder="Phone number"
          aria-describedby={describedByIds}
          value={text}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <button
          ref={buttonRef}
          type="button"
          className="bs-phone-field__country"
          aria-label={`Country code: ${countryName}${code ? ` +${code}` : ''}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          disabled={disabled}
          style={measure ? { left: measure.left, color: measure.color || undefined } : undefined}
          onClick={() => {
            setActiveIndex(0);
            setOpen((wasOpen) => !wasOpen);
          }}
        >
          {/* Flag and code are drawn from data attributes in CSS so a wrapping
              <label> keeps its own text ("Phone number") as the field name.
              The button's accessible name is its aria-label. */}
          <span
            aria-hidden="true"
            className="bs-phone-field__flag"
            data-flag={countryFlag(country)}
          />
          {code ? (
            <span aria-hidden="true" className="bs-phone-field__code" data-code={`+${code}`} />
          ) : null}
          <span aria-hidden="true" className="bs-phone-field__caret" />
        </button>
      </span>
      {showHint ? (
        <span id={hintId} role="status" className="bs-phone-field__hint">
          {PHONE_DOUBLE_CHECK_HINT}
        </span>
      ) : null}
      {open
        ? createPortal(
            <div
              className="bsui-scope bs-phone-field__panel"
              data-bs-theme={measure?.dark ? 'light' : 'dark'}
              style={
                panelRect
                  ? { top: panelRect.top, left: panelRect.left, width: panelRect.width }
                  : undefined
              }
            >
              <input
                ref={searchRef}
                type="search"
                className="bsui-field__control bs-phone-field__search"
                aria-label="Search countries"
                placeholder="Search countries"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleListKey}
              />
              <ul
                id={listId}
                role="listbox"
                aria-label="Choose a country"
                className="bs-phone-field__list"
              >
                {filtered.map((option, index) => (
                  <li
                    key={option.code}
                    role="option"
                    aria-selected={option.code === country}
                    aria-label={`${option.name} +${option.callingCode}`}
                    className={[
                      'bs-phone-field__option',
                      index === activeIndex ? 'bs-phone-field__option--active' : '',
                      option.code === country ? 'bs-phone-field__option--selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => chooseCountry(option.code)}
                  >
                    <span aria-hidden="true">{option.flag}</span>
                    <span className="bs-phone-field__option-name">{option.name}</span>
                    <span className="bs-phone-field__option-code">+{option.callingCode}</span>
                  </li>
                ))}
                {filtered.length === 0 ? (
                  <li className="bs-phone-field__empty" role="presentation">
                    No country matches
                  </li>
                ) : null}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
});

export default PhoneInput;
